'use client';

import { logger } from '@dealerportal/core';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

// script#form-name-variables is always re-executed on route changes regardless
// of annotateScriptLifecycle — its id is stable and unconditionally emitted, so
// we query it directly rather than relying on the opt-in lifecycle attribute.
const ON_ROUTE_CHANGE_SELECTOR =
  'script[data-script-lifecycle="on-route-change"], script#form-name-variables';

let dclShimInstalled = false;

function reRunCtm() {
  const ctm = window.__ctm;
  if (typeof ctm?.main?.runNow === 'function') {
    ctm.main.runNow();
    return;
  }
}

/**
 * Shim `document.addEventListener('DOMContentLoaded', …)` so handlers
 * registered *after* DOMContentLoaded has already fired are invoked
 * immediately. Under Next.js App Router the event fires once on initial paint
 * and never again on client-side navigation, so any CMS script we re-execute
 * that wraps its logic in a DCL handler would silently register a listener
 * that never runs. The shim mirrors what browsers already do for `load` on an
 * already-loaded window: dispatch the callback as a microtask.
 *
 * During initial page load `document.readyState === 'loading'` — the shim is a
 * pure passthrough and the real DOMContentLoaded fires naturally.
 */
function installDCLShim() {
  if (dclShimInstalled) return;
  if (typeof document === 'undefined') return;
  dclShimInstalled = true;
  const original = document.addEventListener.bind(document);
  document.addEventListener = ((
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ) => {
    if (
      type === 'DOMContentLoaded' &&
      document.readyState !== 'loading' &&
      listener != null
    ) {
      Promise.resolve().then(() => {
        try {
          const event = new Event('DOMContentLoaded');
          if (typeof listener === 'function') listener(event);
          else listener.handleEvent(event);
        } catch (e) {
          logger
            .withError(e)
            .warn(
              '[RouteChangeScriptRunner] DOMContentLoaded shim listener threw'
            );
        }
      });
      return;
    }
    return original(type, listener, options);
  }) as typeof document.addEventListener;
}

/**
 * Re-executes an existing tagged `<script>` by cloning its content into a
 * fresh element, appending to `<head>` (browser executes on insertion), then
 * removing. Side effects (globals, listeners, DOM mutations) persist; the DOM
 * tag is a throwaway. Deliberately avoids touching the React-managed source
 * node so React's ownership of its tree is preserved.
 */
function reExecute(source: HTMLScriptElement) {
  const fresh = document.createElement('script');
  if (source.type) fresh.type = source.type;
  if (source.src) {
    if (/cctm\.xyz\/t\.js|tctm\.co\/t\.js/.test(source.src)) {
      reRunCtm();
    } else {
      fresh.src = source.src;
      document.head.appendChild(fresh);
    }
  } else {
    fresh.textContent = source.textContent;
    document.head.appendChild(fresh);
    document.head.removeChild(fresh);
  }
}

/**
 * Watches for client-side route changes and re-runs every CMS script tagged
 * `data-script-lifecycle="on-route-change"`. Skips the initial mount — those
 * scripts already executed as part of the first paint.
 *
 * Pair with `annotateScriptLifecycle: true` on the `reactParserOpts` call
 * that renders CMS-authored HTML (see
 * `libs/ui-components/src/lib/reactOpts.tsx`) so the tags are emitted.
 */
export default function RouteChangeScriptRunner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevUrlRef = useRef<string | null>(null);

  useEffect(() => {
    installDCLShim();
  }, []);

  useEffect(() => {
    const current = pathname + searchParams.toString();
    if (prevUrlRef.current === current) return;
    const isFirstLoad = prevUrlRef.current === null;
    prevUrlRef.current = current;
    if (isFirstLoad) return;

    const scripts = document.querySelectorAll<HTMLScriptElement>(
      ON_ROUTE_CHANGE_SELECTOR
    );

    for (const source of scripts) {
      try {
        reExecute(source);
      } catch (e) {
        logger
          .withError(e)
          .withMetadata({
            pathname,
            preview: source.textContent?.slice(0, 120),
          })
          .warn(
            '[RouteChangeScriptRunner] failed to re-execute on-route-change script'
          );
      }
    }
  }, [pathname, searchParams]);

  return null;
}
